/**
 * Route-level role gate.
 *
 * The sidebar hides admin links, but a hidden link is not access control —
 * the URL is still reachable by typing it. This wraps a route so an
 * under-privileged user gets a plain "not available" page.
 *
 * SCOPE: this is a CLIENT-side gate. It controls what the UI renders, not
 * what the API returns. Anything genuinely sensitive must also be enforced by
 * the worker (stage management is company_admin+ there too).
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { hasMinRole, useAuth, type InnuRole } from '@/lib/auth';
import { usePageTitle } from '@/lib/page-title';
import { PageHeader } from './PageHeader';

/** Same frame and title scale as every settings page, so the gate reads as a page, not a popup (Stage 7 design pass). */
function NotAvailable() {
  usePageTitle('Not available');
  return (
    <div className="max-w-[900px] px-4 py-5 sm:px-6 sm:py-6">
      <PageHeader title="Not available" subtitle="This section is limited to company administrators." />
      <div className="max-w-md rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
        <p className="text-sm text-gray-700">
          If you think you should have access, ask an administrator to check your role in Innu. Everything about
          candidates is still on the board.
        </p>
        <Link
          to="/pipeline"
          className="mt-3 inline-flex min-h-10 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 text-sm font-medium text-gray-800 hover:bg-page"
        >
          <ArrowLeft size={15} strokeWidth={2} aria-hidden /> Back to pipeline
        </Link>
      </div>
    </div>
  );
}

export default function RequireRole({
  role,
  children,
}: {
  /** Minimum role, inclusive. */
  role: InnuRole;
  children: ReactNode;
}) {
  const { user } = useAuth();

  // AuthProvider does not mount children until the token resolves, so `user`
  // is populated by the time any route renders. Null means no session.
  if (!hasMinRole(user, role)) return <NotAvailable />;

  return <>{children}</>;
}
